import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { ASSETS, CONTACT } from "../lib/brand";
import { IconWhatsApp, IconInstagram, IconMapPin } from "./icons";

const LINKS = [
  { to: "/proposito", label: "Propósito" },
  { to: "/sobre", label: "Sobre" },
  { to: "/servicos", label: "Serviços" },
  { to: "/produtos", label: "Produtos" },
  { to: "/loja", label: "Loja" },
  { to: "/depoimentos", label: "Depoimentos" },
  { to: "/contato", label: "Contato" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      <motion.header
        data-testid="site-nav"
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,padding,border-color] duration-500 border-b ${
          scrolled
            ? "bg-cream/85 backdrop-blur-md py-4 border-ink-800/10"
            : "bg-transparent py-6 border-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10 flex items-center justify-between gap-6">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center"
            aria-label="desapegue com Herida — início"
          >
            <img
              src={ASSETS.logo}
              alt="desapegue com Herida"
              className={`w-auto transition-[height] duration-500 ${
                scrolled ? "h-10 sm:h-12" : "h-14 sm:h-16"
              }`}
            />
          </Link>

          <nav className="hidden xl:flex items-center gap-7">
            {LINKS.map((l) => {
              const active = pathname === l.to;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`relative font-sans text-[11px] uppercase tracking-[0.24em] transition-colors duration-300 ${
                    active ? "text-brick-500" : "text-ink-800 hover:text-brick-500"
                  }`}
                >
                  {l.label}
                  {active && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute -bottom-1.5 left-0 right-0 h-px bg-brick-500"
                    />
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noreferrer"
              aria-label="WhatsApp"
              data-testid="nav-whatsapp-cta"
              className="hidden sm:inline-flex items-center justify-center w-10 h-10 border border-ink-800/30 text-ink-800 hover:bg-brick-500 hover:border-brick-500 hover:text-cream transition-colors duration-500"
            >
              <IconWhatsApp size={18} strokeWidth={1.6} />
            </a>
            <a
              href={CONTACT.instagram}
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
              data-testid="nav-instagram-cta"
              className="hidden sm:inline-flex items-center justify-center w-10 h-10 border border-ink-800/30 text-ink-800 hover:bg-brick-500 hover:border-brick-500 hover:text-cream transition-colors duration-500"
            >
              <IconInstagram size={16} strokeWidth={1.6} />
            </a>
            <a
              href={CONTACT.maps}
              target="_blank"
              rel="noreferrer"
              aria-label="Localização"
              data-testid="nav-maps-cta"
              className="hidden sm:inline-flex items-center justify-center w-10 h-10 border border-ink-800/30 text-ink-800 hover:bg-brick-500 hover:border-brick-500 hover:text-cream transition-colors duration-500"
            >
              <IconMapPin size={16} strokeWidth={1.6} />
            </a>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(true)}
              className="xl:hidden font-sans text-[11px] uppercase tracking-[0.24em] text-ink-800 ml-2"
              aria-label="Abrir menu"
            >
              Menu
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[80] bg-brick-500 text-cream xl:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 py-6 border-b border-cream/20">
              <span className="font-serif italic text-2xl">desapegue</span>
              <button
                onClick={() => setOpen(false)}
                className="font-mono text-xs uppercase tracking-[0.24em]"
                data-testid="mobile-menu-close"
              >
                Fechar
              </button>
            </div>
            <nav className="flex flex-col px-6 pt-8 gap-4 overflow-y-auto max-h-[calc(100vh-80px)] pb-10">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.15 + i * 0.05 }}
                >
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="font-serif text-4xl sm:text-5xl leading-none block"
                  >
                    {l.label}.
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mt-6 pt-6 border-t border-cream/30 grid grid-cols-3 gap-3"
              >
                <a
                  href={CONTACT.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="WhatsApp"
                  className="flex items-center justify-center gap-2 border border-cream/40 py-3"
                >
                  <IconWhatsApp size={16} />
                </a>
                <a
                  href={CONTACT.instagram}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Instagram"
                  className="flex items-center justify-center gap-2 border border-cream/40 py-3"
                >
                  <IconInstagram size={16} />
                </a>
                <a
                  href={CONTACT.maps}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Localização"
                  className="flex items-center justify-center gap-2 border border-cream/40 py-3"
                >
                  <IconMapPin size={16} />
                </a>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
